const OAUTH_PROVIDERS = {
    APPLE: 'apple',
    FACEBOOK: 'facebook',
    GOOGLE: 'google',
} as const;

type OAuthProvider = (typeof OAUTH_PROVIDERS)[keyof typeof OAUTH_PROVIDERS];

const OAUTH_SECURITY_FAILURE_REASONS = {
    NONCE_MISMATCH: 'nonce_mismatch',
    NONCE_MISSING: 'nonce_missing',
    SECURE_RANDOM_UNAVAILABLE: 'secure_random_unavailable',
    STATE_MISMATCH: 'state_mismatch',
    STORAGE_UNAVAILABLE: 'storage_unavailable',
} as const;

type OAuthSecurityFailureReason =
    (typeof OAUTH_SECURITY_FAILURE_REASONS)[keyof typeof OAUTH_SECURITY_FAILURE_REASONS];

export class OAuthRequestSecurityError extends Error {
    constructor(
        message: string,
        public readonly provider: OAuthProvider,
        public readonly reason: OAuthSecurityFailureReason
    ) {
        super(message);
        this.name = 'OAuthRequestSecurityError';
        Object.setPrototypeOf(this, OAuthRequestSecurityError.prototype);
    }
}

export function isOAuthRequestSecurityError(error: unknown): error is OAuthRequestSecurityError {
    return error instanceof OAuthRequestSecurityError;
}

const OAUTH_PROVIDER_NAMES: Record<OAuthProvider, string> = {
    [OAUTH_PROVIDERS.APPLE]: 'Apple',
    [OAUTH_PROVIDERS.FACEBOOK]: 'Facebook',
    [OAUTH_PROVIDERS.GOOGLE]: 'Google',
};

type OAuthRequest = {
    state: string;
    nonce?: string;
};

type ConsumedOAuthRequest = {
    expectedNonce?: string;
};

type OAuthCallbackResult =
    | { type: 'failure'; error: Error }
    | { type: 'initial' }
    | { type: 'success'; returnedState: string | null; value: string };

type GetOAuthCallbackResultOptions = {
    provider: OAuthProvider;
    responseParameters: URLSearchParams;
    requiresNonce: boolean;
    successParameter: string;
};

export function beginOAuthRequest(provider: OAuthProvider, requiresNonce: boolean): OAuthRequest {
    const state = createOAuthRequestValue(provider);
    const nonce = requiresNonce ? createOAuthRequestValue(provider) : undefined;
    try {
        sessionStorage.setItem(stateKey(provider), state);
        if (nonce !== undefined) sessionStorage.setItem(nonceKey(provider), nonce);
    } catch {
        clearStoredRequestBestEffort(provider);
        throw oauthSecurityError(provider, OAUTH_SECURITY_FAILURE_REASONS.STORAGE_UNAVAILABLE);
    }
    return nonce === undefined ? { state } : { state, nonce };
}

function createOAuthRequestValue(provider: OAuthProvider): string {
    if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function')
        throw oauthSecurityError(provider, OAUTH_SECURITY_FAILURE_REASONS.SECURE_RANDOM_UNAVAILABLE);

    return Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map((value) => value.toString(16).padStart(2, '0'))
        .join('');
}

export function consumeOAuthRequest(
    provider: OAuthProvider,
    returnedState: string | null,
    requiresNonce: boolean
): ConsumedOAuthRequest {
    let expectedState: string | null;
    let expectedNonce: string | null;
    try {
        expectedState = sessionStorage.getItem(stateKey(provider));
        expectedNonce = sessionStorage.getItem(nonceKey(provider));
    } catch {
        clearStoredRequestBestEffort(provider);
        throw oauthSecurityError(provider, OAUTH_SECURITY_FAILURE_REASONS.STORAGE_UNAVAILABLE);
    }
    if (expectedState === null || returnedState !== expectedState)
        throw oauthSecurityError(provider, OAUTH_SECURITY_FAILURE_REASONS.STATE_MISMATCH);
    if (requiresNonce && expectedNonce === null)
        throw oauthSecurityError(provider, OAUTH_SECURITY_FAILURE_REASONS.NONCE_MISSING);
    clearStoredRequest(provider);
    return expectedNonce === null ? {} : { expectedNonce };
}

export function getOAuthCallbackResult({
    provider,
    responseParameters,
    requiresNonce,
    successParameter,
}: GetOAuthCallbackResultOptions): OAuthCallbackResult {
    const returnedState = responseParameters.get('state');
    if (responseParameters.has('error')) {
        consumeOAuthRequest(provider, returnedState, requiresNonce);
        return {
            type: 'failure',
            error: new Error(`${providerName(provider)} OAuth authorization failed`),
        };
    }

    const value = responseParameters.get(successParameter);
    if (value === null) return { type: 'initial' };
    return { type: 'success', returnedState, value };
}

export function validateOAuthNonce(
    provider: OAuthProvider,
    expectedNonce: string,
    returnedNonce: string | undefined
): void {
    if (returnedNonce !== expectedNonce)
        throw oauthSecurityError(provider, OAUTH_SECURITY_FAILURE_REASONS.NONCE_MISMATCH);
}

const stateKey = (provider: OAuthProvider): string => `${provider}_oauth_state`;

const nonceKey = (provider: OAuthProvider): string => `${provider}_oauth_nonce`;

const providerName = (provider: OAuthProvider): string => OAUTH_PROVIDER_NAMES[provider];

const oauthSecurityError = (
    provider: OAuthProvider,
    reason: OAuthSecurityFailureReason
): OAuthRequestSecurityError => {
    const messages: Record<OAuthSecurityFailureReason, string> = {
        [OAUTH_SECURITY_FAILURE_REASONS.NONCE_MISMATCH]: 'nonce mismatch',
        [OAUTH_SECURITY_FAILURE_REASONS.NONCE_MISSING]: 'nonce missing',
        [OAUTH_SECURITY_FAILURE_REASONS.SECURE_RANDOM_UNAVAILABLE]: 'secure random values unavailable',
        [OAUTH_SECURITY_FAILURE_REASONS.STATE_MISMATCH]: 'state mismatch',
        [OAUTH_SECURITY_FAILURE_REASONS.STORAGE_UNAVAILABLE]: 'session storage unavailable',
    };
    return new OAuthRequestSecurityError(`${providerName(provider)} OAuth ${messages[reason]}`, provider, reason);
};

const clearStoredRequest = (provider: OAuthProvider): void => {
    let removalFailed = false;
    for (const key of [stateKey(provider), nonceKey(provider)]) {
        try {
            sessionStorage.removeItem(key);
        } catch {
            removalFailed = true;
        }
    }
    if (removalFailed) throw oauthSecurityError(provider, OAUTH_SECURITY_FAILURE_REASONS.STORAGE_UNAVAILABLE);
};

const clearStoredRequestBestEffort = (provider: OAuthProvider): void => {
    try {
        sessionStorage.removeItem(stateKey(provider));
        sessionStorage.removeItem(nonceKey(provider));
    } catch {
        // The request cannot be reused when storage is unavailable.
    }
};

export { OAUTH_PROVIDERS };
